import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'
import * as logger from '@owl-nest/logger'

import * as base from './api'

const COMMENT_LIST_CACHE = jotai.atom<hooks.Cache<api.Pageable<base.CommentList<boolean>>>>({})

type CommentsConfig = {
  fetchOnMount?: boolean | 'force'
  params: base.CommentsParams
}

export function toURLSearchParams({ limit, since }: base.CommentsParams): URLSearchParams {
  const searchParams = new URLSearchParams()
  if (limit !== undefined) {
    searchParams.set('limit', String(limit))
  }
  if (since !== undefined) {
    searchParams.set('since', String(since))
  }
  return searchParams
}

type UseNewsComments = {
  createComment: (payload: api.CreateComment) => Promise<api.HttpResult<void> | undefined>
  deleteComment: (commentId: number) => Promise<api.HttpResult<void> | undefined>
  response: hooks.Response<api.Pageable<base.CommentList<boolean>>>
  query: () => Promise<void>
  queryNext: () => Promise<void>
  queryReplies: (commentId: number, limitOverride?: number) => Promise<void>
  replyToComment: (commentId: number, payload: api.CreateComment) => Promise<api.HttpResult<void> | undefined>
}

export function useNewsComments(
  objectId: number | string,
  { fetchOnMount = true, params }: CommentsConfig,
): UseNewsComments {
  const {
    getCurrentResponse,
    patch,
    query: queryComments,
  } = hooks.useSharedQuery((objectId, params) => base.list(objectId, toURLSearchParams(params)), {
    atom: COMMENT_LIST_CACHE,
    id,
  })

  hooks.useFetchOnMount(
    {
      fetchOnMount,
      query,
      getResponse,
    },
    id(objectId, params),
  )

  return { createComment, deleteComment, response: getResponse(), query, queryNext, queryReplies, replyToComment }

  async function createComment(payload: api.CreateComment): Promise<api.HttpResult<void> | undefined> {
    if (id(objectId, params) === undefined) {
      return
    }

    const result = await base.create(objectId, payload)
    return result.next(() => {
      query()
    })
  }

  async function deleteComment(objectId: number | string): Promise<api.HttpResult<void> | undefined> {
    if (id(objectId, params) === undefined) {
      return
    }

    const result = await base.deleteComment(objectId)
    return result.next(() => {
      query()
    })
  }

  function getResponse(): hooks.Response<api.Pageable<base.CommentList<boolean>>> {
    const response = getCurrentResponse([objectId, params])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response
  }

  function id(objectId: number | string, params: base.CommentsParams): string {
    return JSON.stringify([objectId, params.limit])
  }

  async function query(): Promise<void> {
    return queryComments(objectId, params)
  }

  async function queryNext(): Promise<void> {
    const response = getResponse()
    const next = response?.data !== undefined ? (response.data.meta.next ?? undefined) : undefined

    if (next === undefined) {
      console.warn(`[services/comment] 'next' parameter was not provided to queryNext()`)
      return
    }

    const parsedNext = new URLSearchParams(next)

    const limit = parsedNext.get('limit')
    const since = parsedNext.get('since')

    if (!limit || !since) {
      console.warn(`[services/comment] Pagination parameters are missing @ queryNext()`)
      return
    }

    await patch([objectId, params], async (response) => {
      const listResponse = await base.list(objectId, toURLSearchParams({ limit, since }))

      return listResponse.next((success) => {
        let pages = []
        if (response.data !== undefined) {
          pages = [...response.data.comments, ...success.comments]
        } else {
          pages = [...success.comments]
        }

        return {
          comments: pages,
          meta: success.meta,
        }
      })
    })
  }

  async function queryReplies(commentId: number, limitOverride?: number): Promise<void> {
    const response = getResponse()
    const rootComment =
      response?.data !== undefined && response.data.comments.find((comment) => comment.id === commentId)

    if (!rootComment) {
      logger.warn(`[services/comment] Parent comment was not found @ queryReplies()`)
      return
    }

    const next = rootComment.replies_meta?.next
    const parsedNext = new URLSearchParams(next || '')

    const limit = limitOverride ? String(limitOverride) : parsedNext.get('limit')
    const since = parsedNext.get('since') ?? String(Math.floor(Date.now() / 1000))

    if (!limit || !since) {
      logger.warn(`[services/comment] Pagination parameters are missing @ queryReplies()`)
      return
    }

    await patch([objectId, params], async (response) => {
      const getPageResponse = await base.listReplies(commentId, { limit, since })

      return getPageResponse.next((success) => {
        let comments: base.Comment[] = []
        if (response.data !== undefined) {
          const commentIndex = response.data.comments.findIndex((comment) => comment.id === commentId)

          if (commentIndex < 0) {
            logger.warn(`[services/comment] Comment to update was not found @ queryReplies()`)
            return response.data
          }

          comments = [...response.data.comments]
          const comment = comments[commentIndex]

          comments[commentIndex] = {
            ...comments[commentIndex],
            replies: [...(comment.replies ?? []), ...success.comments],
            replies_meta: success.meta,
          }
        }

        return {
          comments: comments,
          meta: success.meta,
        }
      })
    })
  }

  async function replyToComment(
    commentId: number,
    payload: api.CreateComment,
  ): Promise<api.HttpResult<void> | undefined> {
    const result = await base.reply(commentId, payload)

    return result.next(() => {
      queryReplies(commentId, 50)
    })
  }
}
